import type { ContactMethod, HeardFrom, Locale } from '@360face/domain';

/**
 * iPad client form (/anketa, owner 18.10.2026) — all 7 languages. The consent texts are a draft
 * for the lawyer (like the kiosk form): health details are not stored at all, only the outcome.
 */
export interface AnketaMessages {
  title: string;
  welcomeSub: string;
  steps: [string, string, string, string];
  detailsTitle: string;
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  emailHint: string;
  birth: string;
  day: string;
  month: string;
  year: string;
  required: string;
  optional: string;
  prefsTitle: string;
  pressure: string;
  pressureSoft: string;
  pressureStrong: string;
  contact: string;
  contactOptions: Record<ContactMethod, string>;
  heard: string;
  heardOptions: Record<HeardFrom, string>;
  healthTitle: string;
  healthIntro: string;
  conditionsNone: string;
  /** Free comment at the end of the health step (owner 28.10.2026). */
  commentLabel: string;
  commentHint: string;
  conditionsSome: string;
  conditionsCleared: string;
  phoneHint: string;
  /** «Is this you?» for a client who booked online (owner 28.10.2026). */
  phoneBooking: string;
  isThisYou: string;
  yesMe: string;
  notMe: string;
  notMeHint: string;
  otherNumber: string;
  emailOnFile: string;
  yes: string;
  no: string;
  signTitle: string;
  consent: string;
  privacy: string;
  marketing: string;
  signLabel: string;
  clear: string;
  back: string;
  next: string;
  submit: string;
  sending: string;
  thanks: (name: string) => string;
  saved: string;
  tellTherapist: string;
  handBack: string;
  newForm: string;
  errPhone: string;
  errEmail: string;
  errBirth: string;
  errRequired: string;
  errChoose: string;
  errConfirm: string;
  errConsent: string;
  errSign: string;
  errNetwork: string;
  errVersion: string;
  errBusy: string;
  errGeneric: string;
  draft: string;
}

const pt: AnketaMessages = {
  title: 'Ficha de cliente',
  welcomeSub: 'Escolha o idioma',
  steps: ['Dados', 'Preferências', 'Saúde', 'Assinatura'],
  detailsTitle: 'Por favor, preencha os seus dados',
  phone: 'Telemóvel',
  firstName: 'Nome',
  lastName: 'Apelido',
  email: 'E-mail',
  emailHint: 'Para recomendações e lembretes da sua terapeuta',
  birth: 'Data de nascimento',
  day: 'Dia',
  month: 'Mês',
  year: 'Ano',
  required: 'obrigatório',
  optional: 'opcional',
  prefsTitle: 'As suas preferências',
  pressure: 'Pressão da massagem',
  pressureSoft: 'Suave',
  pressureStrong: 'Forte',
  contact: 'Como prefere ser contactada?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Telefone' },
  heard: 'Como nos conheceu?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'Assistente de IA (ChatGPT, Claude…)',
    friend: 'Amigos',
    classpass: 'ClassPass',
    passing_by: 'De passagem',
    other: 'Outro',
  },
  healthTitle: 'Contraindicações',
  healthIntro: 'Leia a lista com atenção. Não precisa de dizer qual — apenas se alguma se aplica a si.',
  conditionsNone: 'Nenhuma se aplica a mim',
  commentLabel: 'Comentário (opcional)',
  commentHint: 'Algo que a equipa deva saber? Ex.: terapeuta preferida, horários que lhe convêm…',
  conditionsSome: 'Algo se aplica — digo à terapeuta antes do tratamento',
  conditionsCleared: 'Algo se aplica, mas tenho autorização do meu médico',
  phoneHint: 'Para contacto quando necessário — não enviamos spam',
  phoneBooking: 'Se já marcou connosco, use o número da marcação',
  isThisYou: 'É você?',
  yesMe: 'Sim, sou eu',
  notMe: 'Não sou eu',
  notMeHint: 'Este número já está registado com outro nome. Por favor, peça ajuda à equipa.',
  otherNumber: 'Outro número',
  emailOnFile: 'E-mail registado',
  yes: 'Sim',
  no: 'Não',
  signTitle: 'Consentimento e assinatura',
  consent:
    'Confirmo que as respostas são verdadeiras e comprometo-me a informar a terapeuta, antes do tratamento, sobre qualquer contraindicação e sobre qualquer alteração do meu estado de saúde. Dou o meu consentimento explícito para que o 360°FACE use as respostas apenas para avaliar se o tratamento é seguro para mim. Não é guardada nenhuma doença ou condição — só a minha resposta. A ficha é apagada ao fim de 24 meses. Posso retirar o consentimento a qualquer momento.',
  privacy:
    'Li a política de privacidade e aceito que o 360°FACE crie a minha ficha de cliente no sistema de marcações (nome, telemóvel, e-mail, data de nascimento).',
  marketing: 'Quero receber novidades e ofertas por e-mail (opcional).',
  signLabel: 'Assine aqui com o dedo',
  clear: 'Limpar',
  back: 'Voltar',
  next: 'Continuar',
  submit: 'Enviar ficha',
  sending: 'A enviar…',
  thanks: (n) => `Obrigado, ${n}!`,
  saved: 'A sua ficha foi guardada.',
  tellTherapist: 'Antes do tratamento, diga à sua terapeuta o que se aplica.',
  handBack: 'Pode devolver o iPad à equipa.',
  newForm: 'Nova ficha',
  errPhone: 'Indique um número de telemóvel válido.',
  errEmail: 'Verifique o e-mail.',
  errBirth: 'Verifique a data de nascimento.',
  errRequired: 'Preencha este campo.',
  errChoose: 'Escolha uma resposta.',
  errConfirm: 'Confirme, por favor, que leu a lista.',
  errConsent: 'Para continuar, aceite os dois consentimentos.',
  errSign: 'Assine, por favor.',
  errNetwork: 'Sem ligação à internet. Tente novamente.',
  errVersion: 'A ficha foi atualizada. Comece de novo, por favor.',
  errBusy: 'Muitas fichas seguidas. Aguarde um minuto.',
  errGeneric: 'Não foi possível guardar. Peça ajuda à equipa.',
  draft: 'Rascunho — textos em revisão',
};

const en: AnketaMessages = {
  title: 'Client form',
  welcomeSub: 'Choose your language',
  steps: ['Details', 'Preferences', 'Health', 'Signature'],
  detailsTitle: 'Please fill in your details',
  phone: 'Mobile',
  firstName: 'First name',
  lastName: 'Surname',
  email: 'E-mail',
  emailHint: 'For your therapist’s recommendations and reminders',
  birth: 'Date of birth',
  day: 'Day',
  month: 'Month',
  year: 'Year',
  required: 'required',
  optional: 'optional',
  prefsTitle: 'Your preferences',
  pressure: 'Massage pressure',
  pressureSoft: 'Gentle',
  pressureStrong: 'Firm',
  contact: 'How should we contact you?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Phone' },
  heard: 'How did you hear about us?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'AI assistant (ChatGPT, Claude…)',
    friend: 'Friends',
    classpass: 'ClassPass',
    passing_by: 'Passing by',
    other: 'Other',
  },
  healthTitle: 'Contraindications',
  healthIntro: 'Please read the list carefully. You don’t need to say which one — only whether any applies to you.',
  conditionsNone: 'None of these applies to me',
  commentLabel: 'Comment (optional)',
  commentHint: 'Anything the team should know? E.g. a preferred therapist, times that suit you…',
  conditionsSome: 'Something applies — I’ll tell my therapist before the treatment',
  conditionsCleared: 'Something applies, but my doctor has cleared me',
  phoneHint: 'Only to reach you when needed — we never send spam',
  phoneBooking: 'If you have booked with us, use the number of your booking',
  isThisYou: 'Is this you?',
  yesMe: "Yes, it's me",
  notMe: "No, it's not me",
  notMeHint: 'This number is already registered under another name. Please ask our team for help.',
  otherNumber: 'Other number',
  emailOnFile: 'E-mail on file',
  yes: 'Yes',
  no: 'No',
  signTitle: 'Consent and signature',
  consent:
    'I confirm my answers are true and undertake to tell my therapist, before the treatment, about any contraindication and any change in my health. I give my explicit consent for 360°FACE to use my answers only to assess whether the treatment is safe for me. No illness or condition is stored — only my answer. The form is deleted after 24 months. I can withdraw my consent at any time.',
  privacy:
    'I have read the privacy policy and agree that 360°FACE creates my client record in its booking system (name, mobile, e-mail, date of birth).',
  marketing: 'I’d like to receive news and offers by e-mail (optional).',
  signLabel: 'Sign here with your finger',
  clear: 'Clear',
  back: 'Back',
  next: 'Continue',
  submit: 'Send form',
  sending: 'Sending…',
  thanks: (n) => `Thank you, ${n}!`,
  saved: 'Your form has been saved.',
  tellTherapist: 'Before the treatment, please tell your therapist what applies.',
  handBack: 'You can hand the iPad back to our team.',
  newForm: 'New form',
  errPhone: 'Please enter a valid mobile number.',
  errEmail: 'Please check the e-mail.',
  errBirth: 'Please check the date of birth.',
  errRequired: 'Please fill in this field.',
  errChoose: 'Please choose an answer.',
  errConfirm: 'Please confirm you have read the list.',
  errConsent: 'To continue, please accept both consents.',
  errSign: 'Please sign.',
  errNetwork: 'No internet connection. Please try again.',
  errVersion: 'The form has been updated. Please start again.',
  errBusy: 'Too many forms in a row. Please wait a minute.',
  errGeneric: 'Could not save. Please ask our team for help.',
  draft: 'Draft — texts under review',
};

const fr: AnketaMessages = {
  title: 'Fiche client',
  welcomeSub: 'Choisissez votre langue',
  steps: ['Coordonnées', 'Préférences', 'Santé', 'Signature'],
  detailsTitle: 'Merci de compléter vos coordonnées',
  phone: 'Portable',
  firstName: 'Prénom',
  lastName: 'Nom',
  email: 'E-mail',
  emailHint: 'Pour les recommandations et rappels de votre praticienne',
  birth: 'Date de naissance',
  day: 'Jour',
  month: 'Mois',
  year: 'Année',
  required: 'obligatoire',
  optional: 'facultatif',
  prefsTitle: 'Vos préférences',
  pressure: 'Pression du massage',
  pressureSoft: 'Douce',
  pressureStrong: 'Forte',
  contact: 'Comment préférez-vous être contactée ?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Téléphone' },
  heard: 'Comment nous avez-vous connus ?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'Assistant IA (ChatGPT, Claude…)',
    friend: 'Des amis',
    classpass: 'ClassPass',
    passing_by: 'En passant',
    other: 'Autre',
  },
  healthTitle: 'Contre-indications',
  healthIntro: 'Lisez attentivement la liste. Inutile de dire laquelle — indiquez seulement si l’une vous concerne.',
  conditionsNone: 'Aucune ne me concerne',
  commentLabel: 'Commentaire (facultatif)',
  commentHint: 'Quelque chose à signaler à l’équipe ? Ex. : praticienne préférée, horaires qui vous conviennent…',
  conditionsSome: 'Quelque chose me concerne — je le dirai à ma praticienne avant le soin',
  conditionsCleared: 'Quelque chose me concerne, mais mon médecin m’a donné son accord',
  phoneHint: 'Uniquement pour vous joindre si nécessaire — jamais de spam',
  phoneBooking: 'Si vous avez déjà réservé, utilisez le numéro de la réservation',
  isThisYou: 'Est-ce vous ?',
  yesMe: 'Oui, c’est moi',
  notMe: 'Non, ce n’est pas moi',
  notMeHint: 'Ce numéro est déjà enregistré à un autre nom. Merci de demander de l’aide à l’équipe.',
  otherNumber: 'Autre numéro',
  emailOnFile: 'E-mail enregistré',
  yes: 'Oui',
  no: 'Non',
  signTitle: 'Consentement et signature',
  consent:
    'Je confirme que mes réponses sont exactes et m’engage à informer ma praticienne, avant le soin, de toute contre-indication et de tout changement de mon état de santé. Je donne mon consentement explicite pour que 360°FACE utilise mes réponses uniquement afin d’évaluer si le soin est sûr pour moi. Aucune maladie ni condition n’est enregistrée — seulement ma réponse. La fiche est supprimée au bout de 24 mois. Je peux retirer mon consentement à tout moment.',
  privacy:
    'J’ai lu la politique de confidentialité et j’accepte que 360°FACE crée ma fiche client dans son système de réservation (nom, portable, e-mail, date de naissance).',
  marketing: 'Je souhaite recevoir les nouveautés et offres par e-mail (facultatif).',
  signLabel: 'Signez ici avec le doigt',
  clear: 'Effacer',
  back: 'Retour',
  next: 'Continuer',
  submit: 'Envoyer la fiche',
  sending: 'Envoi…',
  thanks: (n) => `Merci, ${n} !`,
  saved: 'Votre fiche a été enregistrée.',
  tellTherapist: 'Avant le soin, dites à votre praticienne ce qui vous concerne.',
  handBack: 'Vous pouvez rendre l’iPad à l’équipe.',
  newForm: 'Nouvelle fiche',
  errPhone: 'Indiquez un numéro de portable valide.',
  errEmail: 'Vérifiez l’e-mail.',
  errBirth: 'Vérifiez la date de naissance.',
  errRequired: 'Complétez ce champ.',
  errChoose: 'Choisissez une réponse.',
  errConfirm: 'Merci de confirmer que vous avez lu la liste.',
  errConsent: 'Pour continuer, acceptez les deux consentements.',
  errSign: 'Merci de signer.',
  errNetwork: 'Pas de connexion internet. Réessayez.',
  errVersion: 'La fiche a été mise à jour. Recommencez, s’il vous plaît.',
  errBusy: 'Trop de fiches à la suite. Patientez une minute.',
  errGeneric: 'Enregistrement impossible. Demandez de l’aide à l’équipe.',
  draft: 'Brouillon — textes en cours de validation',
};

const es: AnketaMessages = {
  title: 'Ficha de cliente',
  welcomeSub: 'Elija su idioma',
  steps: ['Datos', 'Preferencias', 'Salud', 'Firma'],
  detailsTitle: 'Por favor, rellene sus datos',
  phone: 'Móvil',
  firstName: 'Nombre',
  lastName: 'Apellido',
  email: 'E-mail',
  emailHint: 'Para las recomendaciones y recordatorios de su terapeuta',
  birth: 'Fecha de nacimiento',
  day: 'Día',
  month: 'Mes',
  year: 'Año',
  required: 'obligatorio',
  optional: 'opcional',
  prefsTitle: 'Sus preferencias',
  pressure: 'Presión del masaje',
  pressureSoft: 'Suave',
  pressureStrong: 'Fuerte',
  contact: '¿Cómo prefiere que la contactemos?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Teléfono' },
  heard: '¿Cómo nos conoció?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'Asistente de IA (ChatGPT, Claude…)',
    friend: 'Amigos',
    classpass: 'ClassPass',
    passing_by: 'Pasaba por aquí',
    other: 'Otro',
  },
  healthTitle: 'Contraindicaciones',
  healthIntro: 'Lea la lista con atención. No hace falta decir cuál — solo si alguna le afecta.',
  conditionsNone: 'Ninguna me afecta',
  commentLabel: 'Comentario (opcional)',
  commentHint: '¿Algo que el equipo deba saber? Ej.: terapeuta preferida, horarios que le vienen bien…',
  conditionsSome: 'Algo me afecta — se lo diré a mi terapeuta antes del tratamiento',
  conditionsCleared: 'Algo me afecta, pero tengo autorización de mi médico',
  phoneHint: 'Solo para contactarle si es necesario — nunca enviamos spam',
  phoneBooking: 'Si ya reservó con nosotros, use el número de la reserva',
  isThisYou: '¿Es usted?',
  yesMe: 'Sí, soy yo',
  notMe: 'No soy yo',
  notMeHint: 'Este número ya está registrado con otro nombre. Por favor, pida ayuda al equipo.',
  otherNumber: 'Otro número',
  emailOnFile: 'E-mail registrado',
  yes: 'Sí',
  no: 'No',
  signTitle: 'Consentimiento y firma',
  consent:
    'Confirmo que mis respuestas son verdaderas y me comprometo a informar a mi terapeuta, antes del tratamiento, de cualquier contraindicación y de cualquier cambio en mi estado de salud. Doy mi consentimiento explícito para que 360°FACE use mis respuestas solo para valorar si el tratamiento es seguro para mí. No se guarda ninguna enfermedad ni condición — solo mi respuesta. La ficha se borra a los 24 meses. Puedo retirar mi consentimiento en cualquier momento.',
  privacy:
    'He leído la política de privacidad y acepto que 360°FACE cree mi ficha de cliente en su sistema de reservas (nombre, móvil, e-mail, fecha de nacimiento).',
  marketing: 'Quiero recibir novedades y ofertas por e-mail (opcional).',
  signLabel: 'Firme aquí con el dedo',
  clear: 'Borrar',
  back: 'Atrás',
  next: 'Continuar',
  submit: 'Enviar ficha',
  sending: 'Enviando…',
  thanks: (n) => `¡Gracias, ${n}!`,
  saved: 'Su ficha se ha guardado.',
  tellTherapist: 'Antes del tratamiento, dígale a su terapeuta lo que le afecta.',
  handBack: 'Puede devolver el iPad al equipo.',
  newForm: 'Nueva ficha',
  errPhone: 'Indique un número de móvil válido.',
  errEmail: 'Revise el e-mail.',
  errBirth: 'Revise la fecha de nacimiento.',
  errRequired: 'Rellene este campo.',
  errChoose: 'Elija una respuesta.',
  errConfirm: 'Por favor, confirme que ha leído la lista.',
  errConsent: 'Para continuar, acepte los dos consentimientos.',
  errSign: 'Firme, por favor.',
  errNetwork: 'Sin conexión a internet. Inténtelo de nuevo.',
  errVersion: 'La ficha se ha actualizado. Empiece de nuevo, por favor.',
  errBusy: 'Demasiadas fichas seguidas. Espere un minuto.',
  errGeneric: 'No se pudo guardar. Pida ayuda al equipo.',
  draft: 'Borrador — textos en revisión',
};

const ru: AnketaMessages = {
  title: 'Анкета клиента',
  welcomeSub: 'Выберите язык',
  steps: ['Данные', 'Предпочтения', 'Здоровье', 'Подпись'],
  detailsTitle: 'Пожалуйста, заполните ваши данные',
  phone: 'Мобильный телефон',
  firstName: 'Имя',
  lastName: 'Фамилия',
  email: 'E-mail',
  emailHint: 'Для рекомендаций и напоминаний от вашего мастера',
  birth: 'Дата рождения',
  day: 'День',
  month: 'Месяц',
  year: 'Год',
  required: 'обязательно',
  optional: 'необязательно',
  prefsTitle: 'Ваши предпочтения',
  pressure: 'Сила нажима',
  pressureSoft: 'Мягко',
  pressureStrong: 'Сильно',
  contact: 'Как с вами лучше связаться?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Телефон' },
  heard: 'Как вы о нас узнали?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'ИИ-ассистент (ChatGPT, Claude…)',
    friend: 'От друзей',
    classpass: 'ClassPass',
    passing_by: 'Проходила мимо',
    other: 'Другое',
  },
  healthTitle: 'Противопоказания',
  healthIntro:
    'Внимательно прочитайте список. Не нужно говорить, что именно, — только относится ли к вам что-то из него.',
  conditionsNone: 'Ничего из списка ко мне не относится',
  commentLabel: 'Комментарий (необязательно)',
  commentHint: 'Что стоит знать команде? Например: любимый мастер, удобное время…',
  conditionsSome: 'Кое-что относится — скажу мастеру перед процедурой',
  conditionsCleared: 'Кое-что относится, но врач разрешил процедуры',
  phoneHint: 'Только для связи при необходимости — никакого спама',
  phoneBooking: 'Если вы уже записывались, укажите номер, с которым записывались',
  isThisYou: 'Это вы?',
  yesMe: 'Да, это я',
  notMe: 'Нет, это не я',
  notMeHint: 'Этот номер уже записан на другое имя. Пожалуйста, обратитесь к мастеру.',
  otherNumber: 'Другой номер',
  emailOnFile: 'E-mail в базе',
  yes: 'Да',
  no: 'Нет',
  signTitle: 'Согласие и подпись',
  consent:
    'Подтверждаю, что ответы верны, и обязуюсь до процедуры сообщить мастеру о любых противопоказаниях и об изменении самочувствия. Даю явное согласие на то, чтобы 360°FACE использовала мои ответы только для оценки безопасности процедуры. Никакие заболевания и состояния не сохраняются — только мой ответ. Анкета удаляется через 24 месяца. Я могу отозвать согласие в любой момент.',
  privacy:
    'Я прочитала политику конфиденциальности и согласна, что 360°FACE создаст мою карточку клиента в системе записи (имя, телефон, e-mail, дата рождения).',
  marketing: 'Хочу получать новости и предложения по e-mail (необязательно).',
  signLabel: 'Распишитесь пальцем здесь',
  clear: 'Стереть',
  back: 'Назад',
  next: 'Продолжить',
  submit: 'Отправить анкету',
  sending: 'Отправляем…',
  thanks: (n) => `Спасибо, ${n}!`,
  saved: 'Ваша анкета сохранена.',
  tellTherapist: 'Перед процедурой скажите мастеру, что именно к вам относится.',
  handBack: 'Можно вернуть iPad сотруднику.',
  newForm: 'Новая анкета',
  errPhone: 'Укажите правильный номер мобильного.',
  errEmail: 'Проверьте e-mail.',
  errBirth: 'Проверьте дату рождения.',
  errRequired: 'Заполните это поле.',
  errChoose: 'Выберите ответ.',
  errConfirm: 'Пожалуйста, подтвердите, что прочитали список.',
  errConsent: 'Чтобы продолжить, отметьте оба согласия.',
  errSign: 'Пожалуйста, распишитесь.',
  errNetwork: 'Нет интернета. Попробуйте ещё раз.',
  errVersion: 'Анкета обновилась. Пожалуйста, начните заново.',
  errBusy: 'Слишком много анкет подряд. Подождите минуту.',
  errGeneric: 'Не удалось сохранить. Попросите помощи у сотрудника.',
  draft: 'Черновик — тексты на проверке',
};

const uk: AnketaMessages = {
  title: 'Анкета клієнта',
  welcomeSub: 'Оберіть мову',
  steps: ['Дані', 'Уподобання', 'Здоровʼя', 'Підпис'],
  detailsTitle: 'Будь ласка, заповніть ваші дані',
  phone: 'Мобільний телефон',
  firstName: 'Імʼя',
  lastName: 'Прізвище',
  email: 'E-mail',
  emailHint: 'Для рекомендацій і нагадувань від вашого майстра',
  birth: 'Дата народження',
  day: 'День',
  month: 'Місяць',
  year: 'Рік',
  required: 'обовʼязково',
  optional: 'необовʼязково',
  prefsTitle: 'Ваші уподобання',
  pressure: 'Сила натиску',
  pressureSoft: 'Мʼяко',
  pressureStrong: 'Сильно',
  contact: 'Як з вами краще звʼязатися?',
  contactOptions: { email: 'E-mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Телефон' },
  heard: 'Як ви про нас дізналися?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'ШІ-асистент (ChatGPT, Claude…)',
    friend: 'Від друзів',
    classpass: 'ClassPass',
    passing_by: 'Проходила повз',
    other: 'Інше',
  },
  healthTitle: 'Протипоказання',
  healthIntro: 'Уважно прочитайте список. Не потрібно казати, що саме, — лише чи стосується вас щось із нього.',
  conditionsNone: 'Нічого зі списку мене не стосується',
  commentLabel: 'Коментар (необов’язково)',
  commentHint: 'Що варто знати команді? Наприклад: улюблений майстер, зручний час…',
  conditionsSome: 'Дещо стосується — скажу майстру перед процедурою',
  conditionsCleared: 'Дещо стосується, але лікар дозволив процедури',
  phoneHint: 'Лише для звʼязку за потреби — жодного спаму',
  phoneBooking: 'Якщо ви вже записувалися, вкажіть номер, з яким записувалися',
  isThisYou: 'Це ви?',
  yesMe: 'Так, це я',
  notMe: 'Ні, це не я',
  notMeHint: 'Цей номер уже записаний на інше імʼя. Будь ласка, зверніться до майстра.',
  otherNumber: 'Інший номер',
  emailOnFile: 'E-mail у базі',
  yes: 'Так',
  no: 'Ні',
  signTitle: 'Згода та підпис',
  consent:
    'Підтверджую, що відповіді правдиві, і зобовʼязуюся до процедури повідомити майстра про будь-які протипоказання та про зміну самопочуття. Даю явну згоду на те, щоб 360°FACE використовувала мої відповіді лише для оцінки безпечності процедури. Жодні захворювання чи стани не зберігаються — лише моя відповідь. Анкета видаляється через 24 місяці. Я можу відкликати згоду будь-коли.',
  privacy:
    'Я прочитала політику конфіденційності та погоджуюся, що 360°FACE створить мою картку клієнта в системі запису (імʼя, телефон, e-mail, дата народження).',
  marketing: 'Хочу отримувати новини та пропозиції на e-mail (необовʼязково).',
  signLabel: 'Розпишіться пальцем тут',
  clear: 'Стерти',
  back: 'Назад',
  next: 'Продовжити',
  submit: 'Надіслати анкету',
  sending: 'Надсилаємо…',
  thanks: (n) => `Дякуємо, ${n}!`,
  saved: 'Вашу анкету збережено.',
  tellTherapist: 'Перед процедурою скажіть майстру, що саме вас стосується.',
  handBack: 'Можна повернути iPad працівнику.',
  newForm: 'Нова анкета',
  errPhone: 'Вкажіть правильний номер мобільного.',
  errEmail: 'Перевірте e-mail.',
  errBirth: 'Перевірте дату народження.',
  errRequired: 'Заповніть це поле.',
  errChoose: 'Оберіть відповідь.',
  errConfirm: 'Будь ласка, підтвердьте, що прочитали список.',
  errConsent: 'Щоб продовжити, позначте обидві згоди.',
  errSign: 'Будь ласка, розпишіться.',
  errNetwork: 'Немає інтернету. Спробуйте ще раз.',
  errVersion: 'Анкету оновлено. Будь ласка, почніть знову.',
  errBusy: 'Забагато анкет поспіль. Зачекайте хвилину.',
  errGeneric: 'Не вдалося зберегти. Попросіть допомоги в працівника.',
  draft: 'Чернетка — тексти на перевірці',
};

const de: AnketaMessages = {
  title: 'Kundenformular',
  welcomeSub: 'Wählen Sie Ihre Sprache',
  steps: ['Angaben', 'Vorlieben', 'Gesundheit', 'Unterschrift'],
  detailsTitle: 'Bitte füllen Sie Ihre Angaben aus',
  phone: 'Handynummer',
  firstName: 'Vorname',
  lastName: 'Nachname',
  email: 'E-Mail',
  emailHint: 'Für Empfehlungen und Erinnerungen Ihrer Therapeutin',
  birth: 'Geburtsdatum',
  day: 'Tag',
  month: 'Monat',
  year: 'Jahr',
  required: 'Pflichtfeld',
  optional: 'optional',
  prefsTitle: 'Ihre Vorlieben',
  pressure: 'Massagedruck',
  pressureSoft: 'Sanft',
  pressureStrong: 'Kräftig',
  contact: 'Wie dürfen wir Sie kontaktieren?',
  contactOptions: { email: 'E-Mail', whatsapp: 'WhatsApp', telegram: 'Telegram', phone: 'Telefon' },
  heard: 'Wie haben Sie von uns erfahren?',
  heardOptions: {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'KI-Assistent (ChatGPT, Claude…)',
    friend: 'Freunde',
    classpass: 'ClassPass',
    passing_by: 'Im Vorbeigehen',
    other: 'Sonstiges',
  },
  healthTitle: 'Kontraindikationen',
  healthIntro:
    'Bitte lesen Sie die Liste aufmerksam. Sie müssen nicht sagen, was genau — nur, ob etwas davon auf Sie zutrifft.',
  conditionsNone: 'Nichts davon trifft auf mich zu',
  commentLabel: 'Kommentar (optional)',
  commentHint: 'Soll das Team etwas wissen? Z. B. Wunsch-Therapeutin, passende Zeiten…',
  conditionsSome: 'Etwas trifft zu — ich sage es meiner Therapeutin vor der Behandlung',
  conditionsCleared: 'Etwas trifft zu, aber mein Arzt hat die Behandlung erlaubt',
  phoneHint: 'Nur, um Sie bei Bedarf zu erreichen — kein Spam',
  phoneBooking: 'Wenn Sie schon gebucht haben, nutzen Sie die Nummer der Buchung',
  isThisYou: 'Sind Sie das?',
  yesMe: 'Ja, das bin ich',
  notMe: 'Nein, das bin ich nicht',
  notMeHint: 'Diese Nummer ist bereits auf einen anderen Namen registriert. Bitte fragen Sie unser Team.',
  otherNumber: 'Andere Nummer',
  emailOnFile: 'E-Mail hinterlegt',
  yes: 'Ja',
  no: 'Nein',
  signTitle: 'Einwilligung und Unterschrift',
  consent:
    'Ich bestätige, dass meine Antworten wahr sind, und verpflichte mich, meiner Therapeutin vor der Behandlung jede Kontraindikation und jede Veränderung meines Gesundheitszustands mitzuteilen. Ich willige ausdrücklich ein, dass 360°FACE meine Antworten nur verwendet, um zu beurteilen, ob die Behandlung für mich sicher ist. Es wird keine Erkrankung und kein Zustand gespeichert — nur meine Antwort. Das Formular wird nach 24 Monaten gelöscht. Ich kann meine Einwilligung jederzeit widerrufen.',
  privacy:
    'Ich habe die Datenschutzerklärung gelesen und bin einverstanden, dass 360°FACE meine Kundenkarte im Buchungssystem anlegt (Name, Handynummer, E-Mail, Geburtsdatum).',
  marketing: 'Ich möchte Neuigkeiten und Angebote per E-Mail erhalten (optional).',
  signLabel: 'Bitte hier mit dem Finger unterschreiben',
  clear: 'Löschen',
  back: 'Zurück',
  next: 'Weiter',
  submit: 'Formular senden',
  sending: 'Wird gesendet…',
  thanks: (n) => `Vielen Dank, ${n}!`,
  saved: 'Ihr Formular wurde gespeichert.',
  tellTherapist: 'Bitte sagen Sie Ihrer Therapeutin vor der Behandlung, was zutrifft.',
  handBack: 'Sie können das iPad dem Team zurückgeben.',
  newForm: 'Neues Formular',
  errPhone: 'Bitte geben Sie eine gültige Handynummer ein.',
  errEmail: 'Bitte prüfen Sie die E-Mail.',
  errBirth: 'Bitte prüfen Sie das Geburtsdatum.',
  errRequired: 'Bitte füllen Sie dieses Feld aus.',
  errChoose: 'Bitte wählen Sie eine Antwort.',
  errConfirm: 'Bitte bestätigen Sie, dass Sie die Liste gelesen haben.',
  errConsent: 'Bitte stimmen Sie beiden Einwilligungen zu.',
  errSign: 'Bitte unterschreiben Sie.',
  errNetwork: 'Keine Internetverbindung. Bitte erneut versuchen.',
  errVersion: 'Das Formular wurde aktualisiert. Bitte beginnen Sie neu.',
  errBusy: 'Zu viele Formulare hintereinander. Bitte eine Minute warten.',
  errGeneric: 'Speichern nicht möglich. Bitte wenden Sie sich an das Team.',
  draft: 'Entwurf — Texte in Prüfung',
};

export const ANKETA_MESSAGES: Record<Locale, AnketaMessages> = { pt, en, fr, es, ru, uk, de };

/**
 * Languages offered on the iPad (owner 19.10.2026: German hidden for now — six buttons fit the
 * welcome grid; the German texts stay ready).
 */
export const ANKETA_LOCALES: readonly Locale[] = ['pt', 'en', 'fr', 'es', 'ru', 'uk'];
